import { memo, type ReactNode, useCallback, useMemo } from 'react'

import { DropdownMenu, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'

import type { DataTableFilterDropdownProps } from './data-table.interface'
import { DataTableFilterContent } from './data-table-filter-content.atom'
import { DataTableFilterTrigger } from './data-table-filter-trigger.atom'

/**
 * A multi-select filter dropdown for data table columns.
 */
const DataTableFilterDropdownBase: React.FC<DataTableFilterDropdownProps> = ({
  filter,
  selectedValueList,
  onChange,
  className,
}): ReactNode => {
  const handleToggle = useCallback(
    (value: string): void => {
      const newValueList = selectedValueList.includes(value)
        ? selectedValueList.filter((v) => v !== value)
        : [...selectedValueList, value]
      onChange(newValueList)
    },
    [onChange, selectedValueList]
  )

  const handleClear = useCallback((): void => {
    onChange([])
  }, [onChange])

  const selectedOptionList = useMemo(
    () => filter.optionList.filter((opt) => selectedValueList.includes(opt.value)),
    [filter.optionList, selectedValueList]
  )

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <DataTableFilterTrigger
          label={filter.label}
          selectedOptionList={selectedOptionList}
          className={className}
        />
      </DropdownMenuTrigger>
      <DataTableFilterContent
        label={filter.label}
        optionList={filter.optionList}
        selectedValueList={selectedValueList}
        onToggle={handleToggle}
        onClear={handleClear}
      />
    </DropdownMenu>
  )
}

export const DataTableFilterDropdown = memo(DataTableFilterDropdownBase)

DataTableFilterDropdown.displayName = 'DataTableFilterDropdown'
